/**
 * Cámara del celular como lectora de barras (móvil / táctil).
 */

const SCAN_FORMATS = [
	'ean_13',
	'ean_8',
	'upc_a',
	'upc_e',
	'code_128',
	'code_39',
	'codabar',
	'itf',
	'qr_code',
];

function pluginUrl(win) {
	const cfg = win.POSKN_API_CONFIG || {};
	const raw = cfg.pluginUrl || win.posknPluginUrl || '';
	return String(raw).replace(/\/?$/, '/');
}

function stopStream(stream) {
	if (!stream) return;
	stream.getTracks().forEach((t) => {
		try {
			t.stop();
		} catch (e) {
			/* ignore */
		}
	});
}

function loadZXing(win) {
	if (win.ZXing) {
		return Promise.resolve(win.ZXing);
	}
	const local = pluginUrl(win) + 'js/zxing.min.js';
	const cdn = 'https://cdn.jsdelivr.net/npm/@zxing/library@0.21.3/umd/index.min.js';
	const trySrc = (src) =>
		new Promise((resolve, reject) => {
			const s = win.document.createElement('script');
			s.src = src;
			s.async = true;
			s.onload = () => (win.ZXing ? resolve(win.ZXing) : reject(new Error('ZXing no cargó')));
			s.onerror = () => reject(new Error('No se pudo cargar ZXing'));
			win.document.head.appendChild(s);
		});
	return trySrc(local).catch(() => trySrc(cdn));
}

async function detectWithBarcodeDetector(win, video, onCode) {
	if (!('BarcodeDetector' in win)) {
		return null;
	}
	let detector;
	try {
		detector = new win.BarcodeDetector({ formats: SCAN_FORMATS });
	} catch (e) {
		try {
			detector = new win.BarcodeDetector();
		} catch (e2) {
			return null;
		}
	}
	let running = true;
	const loop = async () => {
		if (!running || video.readyState < 2) {
			if (running) win.requestAnimationFrame(loop);
			return;
		}
		try {
			const codes = await detector.detect(video);
			if (codes && codes.length) {
				const raw = String(codes[0].rawValue || '').trim();
				if (raw) {
					running = false;
					onCode(raw);
					return;
				}
			}
		} catch (e) {
			/* frame skip */
		}
		if (running) win.requestAnimationFrame(loop);
	};
	win.requestAnimationFrame(loop);
	return () => {
		running = false;
	};
}

async function detectWithZXing(win, video, stream, onCode) {
	const ZXing = await loadZXing(win);
	const reader = new ZXing.BrowserMultiFormatReader();
	let stopped = false;
	const handler = (result) => {
		if (stopped || !result) return;
		const raw = String(result.getText ? result.getText() : result.text || '').trim();
		if (!raw) return;
		stopped = true;
		try {
			reader.reset();
		} catch (e) {
			/* ignore */
		}
		onCode(raw);
	};
	if (typeof reader.decodeFromStream === 'function' && stream) {
		await reader.decodeFromStream(stream, video, handler);
	} else if (typeof reader.decodeFromVideoElementContinuously === 'function') {
		reader.decodeFromVideoElementContinuously(video, handler);
	} else {
		await reader.decodeFromVideoDevice(undefined, video, handler);
	}
	return () => {
		stopped = true;
		try {
			reader.reset();
		} catch (e2) {
			/* ignore */
		}
	};
}

export function initBarcodeCamera(win, $) {
	if (win._posknBarcodeCameraBound) return;
	win._posknBarcodeCameraBound = true;

	let stream = null;
	let stopDetect = null;
	let lastCode = '';
	let lastAt = 0;

	const overlay = () => win.document.getElementById('poskn-scan-overlay');
	const videoEl = () => win.document.getElementById('poskn-scan-video');

	const cerrar = () => {
		if (typeof stopDetect === 'function') {
			try {
				stopDetect();
			} catch (e) {
				/* ignore */
			}
		}
		stopDetect = null;
		stopStream(stream);
		stream = null;
		const ov = overlay();
		const vid = videoEl();
		if (vid) {
			vid.srcObject = null;
		}
		if (ov) {
			ov.classList.remove('is-open');
			ov.setAttribute('aria-hidden', 'true');
		}
	};

	const aplicar = (raw) => {
		const now = Date.now();
		if (raw === lastCode && now - lastAt < 1500) {
			return;
		}
		lastCode = raw;
		lastAt = now;
		cerrar();
		if (typeof win.posknAplicarCodigoBarras === 'function') {
			const ok = win.posknAplicarCodigoBarras(raw);
			if (!ok && typeof win.mostrarNotificacion === 'function') {
				win.mostrarNotificacion('Código leído: ' + raw + '. No hay producto con ese SKU.', 'error');
			}
		}
	};

	const abrir = async () => {
		if (!win.isSecureContext && win.location.hostname !== 'localhost' && win.location.hostname !== '127.0.0.1') {
			if (typeof win.mostrarNotificacion === 'function') {
				win.mostrarNotificacion('La cámara del celular requiere HTTPS. Abre el POS por el dominio seguro.', 'error');
			}
			return;
		}
		if (!win.navigator.mediaDevices || !win.navigator.mediaDevices.getUserMedia) {
			if (typeof win.mostrarNotificacion === 'function') {
				win.mostrarNotificacion('Este navegador no permite usar la cámara.', 'error');
			}
			return;
		}
		const ov = overlay();
		const vid = videoEl();
		if (!ov || !vid) return;

		try {
			stream = await win.navigator.mediaDevices.getUserMedia({
				audio: false,
				video: {
					facingMode: { ideal: 'environment' },
					width: { ideal: 1280 },
					height: { ideal: 720 },
				},
			});
		} catch (e) {
			if (typeof win.mostrarNotificacion === 'function') {
				win.mostrarNotificacion('No se pudo abrir la cámara. Revisa el permiso en el celular.', 'error');
			}
			return;
		}

		vid.srcObject = stream;
		vid.setAttribute('playsinline', 'true');
		vid.muted = true;
		try {
			await vid.play();
		} catch (e) {
			/* autoplay flags */
		}
		ov.classList.add('is-open');
		ov.setAttribute('aria-hidden', 'false');

		const onCode = (raw) => aplicar(raw);
		try {
			stopDetect = await detectWithBarcodeDetector(win, vid, onCode);
			if (!stopDetect) {
				stopDetect = await detectWithZXing(win, vid, stream, onCode);
			}
		} catch (e) {
			cerrar();
			if (typeof win.mostrarNotificacion === 'function') {
				win.mostrarNotificacion('No se pudo iniciar el lector de cámara en este navegador.', 'error');
			}
		}
	};

	$(document).on('click', '#btn-escanear-camara', function onScanCamClick(e) {
		e.preventDefault();
		abrir();
	});
	$(document).on('click', '#poskn-scan-cerrar', function onScanClose(e) {
		e.preventDefault();
		cerrar();
	});
	$(document).on('keydown', function onScanEsc(e) {
		if (e.key === 'Escape' && overlay() && overlay().classList.contains('is-open')) {
			cerrar();
		}
	});
}
